import type { DrillVesselAnalytics } from '@felix/domain';
import { sheetsWorkbook } from '../reports/xlsx.js';
import type { DrillAnalyticsData } from './analytics-types.js';
import { formatSeconds } from './record-view.js';
import { safeFileBase } from './report-pdf.js';

type T = (key: string, options?: Record<string, unknown>) => string;

const time = (seconds: number | null) => (seconds === null ? null : formatSeconds(seconds));

/**
 * Filo analitiğinin Excel çıktısı (ADR 0128 Faz 5 Ek): Gemiler (karşılaştırma ve filo satırı),
 * Eğilim (gemi × ay ortalamaları) ve Tatbikat türleri. Sayılar sayı hücresi, süreler `m:ss`.
 */
export function analyticsSheets(data: DrillAnalyticsData, t: T) {
  const c = (key: string) => t(`drillReport.analytics.columns.${key}`);
  const fleet = t('drillReport.analytics.fleet');
  const row = (v: DrillVesselAnalytics) => [
    v.vesselName ?? fleet,
    v.planned,
    v.completed,
    v.completionRate.percent,
    v.late,
    v.overdue,
    time(v.musterAvgSeconds),
    time(v.responseAvgSeconds),
    v.openFindings,
  ];
  const all = [...data.vessels, ...(data.vessels.length > 1 ? [data.fleet] : [])];
  return [
    {
      name: t('drillReport.analytics.sheets.vessels'),
      head: [
        c('vessel'),
        c('planned'),
        c('completed'),
        `${c('completionRate')} (%)`,
        c('late'),
        c('overdue'),
        c('muster'),
        c('response'),
        c('openFindings'),
      ],
      rows: all.map(row),
    },
    {
      name: t('drillReport.analytics.sheets.trend'),
      head: [c('vessel'), c('month'), c('records'), c('muster'), c('response')],
      rows: all.flatMap((v) =>
        v.trend.map((p) => [
          v.vesselName ?? fleet,
          p.month,
          p.records,
          time(p.musterAvgSeconds),
          time(p.responseAvgSeconds),
        ]),
      ),
    },
    {
      name: t('drillReport.analytics.sheets.types'),
      head: [c('type'), c('count'), `${c('share')} (%)`],
      rows: data.types.map((s) => [s.categoryName ?? '—', s.count, s.percent]),
    },
  ];
}

export async function analyticsWorkbook(data: DrillAnalyticsData, t: T): Promise<Uint8Array> {
  return sheetsWorkbook(analyticsSheets(data, t));
}

/** Dosya adı: "drill-analytics-2026-08-01-2026-10-31.xlsx" (gemide de kaydedilebilir). */
export function analyticsFileName(data: DrillAnalyticsData): string {
  return `${safeFileBase(`drill-analytics-${data.range.from}-${data.range.to}`)}.xlsx`;
}
